import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { API_URLS } from '../constants/api';

let db = null;
try {
  const firebaseConfig = require('../config/firebase');
  db = firebaseConfig.db;
} catch (error) {
  console.warn(
    'Firebase not configured yet. Stripe integration will be limited.'
  );
}

const StripeContext = createContext();

export function useStripe() {
  return useContext(StripeContext);
}

export function StripeProvider({ children }) {
  const { currentUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [lifetimeAccess, setLifetimeAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Use a ref to track subscription for the interval callback without causing re-renders
  const subscriptionRef = useRef(null);

  // Check if subscription is disabled for testing
  const subscriptionDisabled =
    process.env.REACT_APP_SUBSCRIPTION_DISABLED === 'true';

  // Check if perpetual trial mode is enabled
  const perpetualTrialMode =
    process.env.REACT_APP_PERPETUAL_TRIAL_MODE === 'true';

  // Check if authentication is bypassed for development
  const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

  // Load user's subscription data with real-time updates
  useEffect(() => {
    if (!currentUser && !skipAuth) {
      setLoading(false);
      return;
    }

    setLoading(true);

    if (subscriptionDisabled || perpetualTrialMode || skipAuth) {
      // Create mock subscription for testing, perpetual trial, or auth bypass
      const mode = subscriptionDisabled
        ? 'testing'
        : perpetualTrialMode
          ? 'perpetual trial'
          : 'auth bypass';
      const trialDays = perpetualTrialMode ? 365 : 14; // 1 year for perpetual trial, 14 days for testing

      const mockSubscription = {
        id: 'sub_mock_testing',
        status: 'trialing',
        current_period_start: Math.floor(Date.now() / 1000),
        current_period_end:
          Math.floor(Date.now() / 1000) + trialDays * 24 * 60 * 60,
        trial_start: Math.floor(Date.now() / 1000),
        trial_end: Math.floor(Date.now() / 1000) + trialDays * 24 * 60 * 60,
        cancel_at_period_end: false,
        customer: 'cus_mock_testing'
      };
      setSubscription(mockSubscription);
      subscriptionRef.current = mockSubscription;
      setLoading(false);
      console.log(`Using mock subscription for ${mode}`);
      return;
    }

    if (!db) {
      console.log('No Firestore database available');
      setLoading(false);
      return;
    }

    console.log('📡 Setting up real-time subscription listener');

    // Set up real-time listener - automatically updates when Stripe webhooks update Firestore
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (userDoc) => {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('📡 Subscription updated from Firestore');
          const newSubscription = userData.subscription || null;
          setSubscription(newSubscription);
          subscriptionRef.current = newSubscription;
          setLifetimeAccess(userData.lifetimeAccess === true);
        } else {
          console.log('No user document found');
          setSubscription(null);
          subscriptionRef.current = null;
          setLifetimeAccess(false);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to subscription:', err);
        setError('Failed to load subscription data');
        setLoading(false);
      }
    );

    // Periodic refresh from Stripe (every 5 minutes)
    // This catches changes that webhooks might miss (e.g., test clock advancement)
    const refreshInterval = setInterval(
      async () => {
        // Use ref to get the latest subscription value without causing re-renders
        const currentSub = subscriptionRef.current;
        if (currentSub?.id) {
          console.log('⏱️ Periodic subscription refresh from Stripe...');
          try {
            const response = await fetch(API_URLS.REFRESH_SUBSCRIPTION, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ subscriptionId: currentSub.id })
            });

            if (response.ok) {
              console.log('✅ Background refresh complete');
            } else {
              console.log('⚠️ Background refresh failed, will retry in 5 min');
            }
          } catch (err) {
            console.log('⚠️ Background refresh error:', err.message);
          }
        }
      },
      5 * 60 * 1000
    ); // 5 minutes

    // Cleanup listener and interval on unmount
    return () => {
      console.log('Cleaning up subscription listener and refresh interval');
      unsubscribe();
      clearInterval(refreshInterval);
    };
  }, [currentUser, subscriptionDisabled, skipAuth, perpetualTrialMode]);

  // Update subscription status
  // Note: Backend now handles Firebase sync automatically via webhooks
  // This just updates local state for immediate UI feedback
  const updateSubscription = async (subscriptionData) => {
    if (!currentUser) {
      console.error('updateSubscription: No current user');
      return;
    }

    console.log('updateSubscription called with:', subscriptionData);

    // Backend now syncs to Firebase automatically
    // Just update local state for immediate UI feedback
    setSubscription(subscriptionData);
    subscriptionRef.current = subscriptionData;
    console.log(
      'Updated local subscription state (backend handles Firebase sync)'
    );
  };

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    // Check for lifetime access first
    if (lifetimeAccess) {
      console.log('hasActiveSubscription: Lifetime access granted');
      return true;
    }

    if (!subscription) {
      console.log('hasActiveSubscription: No subscription');
      return false;
    }

    const now = new Date();
    // Handle both camelCase (from backend) and snake_case (from Firestore) field names
    const endDate = new Date(
      (subscription.current_period_end || subscription.currentPeriodEnd) * 1000
    );

    // Consider both 'active' and 'trialing' as valid subscription statuses
    const isActive =
      (subscription.status === 'active' ||
        subscription.status === 'trialing') &&
      endDate > now;

    // Debug logging removed for privacy

    return isActive;
  };

  // Check if user is on free tier (no subscription or inactive subscription)
  const isOnFreeTier = () => {
    return !hasActiveSubscription();
  };

  // Get subscription status
  const getSubscriptionStatus = () => {
    if (!subscription) return 'free';
    if (hasActiveSubscription()) {
      if (subscription.status === 'trialing') return 'trial';
      return 'active';
    }
    if (subscription.status === 'past_due') return 'past_due';
    if (subscription.status === 'canceled') return 'canceled';
    return 'free';
  };

  // Get trial days remaining (for Stripe trials)
  const getTrialDaysRemaining = () => {
    if (!subscription || subscription.status !== 'trialing') return 0;

    const now = new Date();
    const trialEnd = new Date(
      (subscription.trial_end || subscription.trialEnd) * 1000
    );
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Reload subscription from Firestore
  const reloadSubscription = useCallback(async () => {
    if (!currentUser || !db) return;

    try {
      console.log('Reloading subscription from Firestore...');
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('Reloaded subscription data from Firestore');
        const newSubscription = userData.subscription || null;
        setSubscription(newSubscription);
        subscriptionRef.current = newSubscription;
        setLifetimeAccess(userData.lifetimeAccess === true);
      }
    } catch (err) {
      console.error('Error reloading subscription:', err);
    }
  }, [currentUser]);

  const value = {
    subscription,
    lifetimeAccess,
    loading,
    error,
    updateSubscription,
    reloadSubscription,
    hasActiveSubscription,
    isOnFreeTier,
    getSubscriptionStatus,
    getTrialDaysRemaining,
    perpetualTrialMode
  };

  return (
    <StripeContext.Provider value={value}>{children}</StripeContext.Provider>
  );
}

StripeProvider.propTypes = {
  children: PropTypes.node.isRequired
};
